import { Injectable, OnDestroy } from '@angular/core';
import { map, mergeMap } from 'rxjs';

import {
    GroupedAuditableModel,
    ItemRow,
    LeftSidebarManagementService,
    LhpManagementConfig,
    TypeLhpItem
} from '../../../Common/Layout/services/left-sidebar-management.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { DiscoverLeftSidebarService } from './discover-left-sidebar.service';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { DistributionStatus } from '../../../Common/TargetingShared/enum/distribution-status.enum';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { TargetedListTypesEnum } from '../../../Common/TargetingShared/enum/targeted-list-types.enum';
import { TargetingFiltersEnum } from '../../../Common/TargetingShared/enum/targeting-filters.enum';
import { TargetingListList } from '../../Targeting/models/targeting-list-list.model';
import { TargetingSearchList } from '../../Targeting/models/targeting-search-list.model';
import { DistributionSummaryModel } from '../../../Common/TargetingShared/model/distribution-summary-model';
import { Mode } from '../../../Common/Layout/enums/mode.enum';
import { TargetingSearchesService } from '../../Targeting/services/targeting-searches.service';
import { TargetingListService } from '../../Targeting/services/targeting-list.service';
import { DistributionService } from '../../../Common/TargetingShared/services/distribution.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';

type TargetingListLhpItem = GroupedAuditableModel & {count: number; hasContacts: boolean; hasOutlets: boolean};

@Injectable()
export class DiscoverLeftSidebarManagementService extends LeftSidebarManagementService implements OnDestroy {

    private readonly listTypeFilter = new FilterTypeCheckboxModel<TargetedListTypesEnum>(
        'listType',
        'Discover.leftSidebar.filter.listType',
        [
            {
                label: 'Discover.leftSidebar.filter.listType.option.both',
                value: TargetedListTypesEnum.both
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.contact',
                value: TargetedListTypesEnum.contact
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.outlet',
                value: TargetedListTypesEnum.outlet
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.empty',
                value: TargetedListTypesEnum.empty
            }
        ]
    );

    private readonly searchTypeFilter = new FilterTypeCheckboxModel<SearchModeEnum>(
        'searchType',
        'Discover.leftSidebar.filter.searchType',
        [
            {
                label: 'Discover.leftSidebar.filter.searchType.option.contact',
                value: SearchModeEnum.contact
            },
            {
                label: 'Discover.leftSidebar.filter.searchType.option.outlet',
                value: SearchModeEnum.outlet
            }
        ]
    );


    private readonly deliveryStatusFilter = new FilterTypeCheckboxModel<DistributionStatus>(
        TargetingFiltersEnum.status,
        'Discover.targeting.filter.status',
        [
            {
                label: 'Discover.deliveryStatusFilter.draft',
                value: DistributionStatus.draft
            },
            {
                label: 'Discover.deliveryStatusFilter.scheduled',
                value: DistributionStatus.scheduled
            },
            {
                label: 'Discover.deliveryStatusFilter.sent',
                value: DistributionStatus.sent
            },
            {
                label: 'Discover.deliveryStatusFilter.failed',
                value: DistributionStatus.failed
            },
        ]
    );

    private modeConfigMap: Map<Mode, LhpManagementConfig> = new Map<Mode, LhpManagementConfig>([
        [
            Mode.searches,
            {
                itemSource: this.targetingSearchService.all$,
                columns: [
                    this.itemNameColumn,
                    this.typeCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.searchTypeFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getSearchTypeFilterCounts(this.allListItems as TargetingSearchList[], this.searchTypeFilter);
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applySearchTypeFitler(filteredItems as TargetingSearchList[], this.searchTypeFilter);
                },
                resetCustomFilters: () => {
                    this.searchTypeFilter.setValue([]);
                }
            }
        ],
        [
            Mode.discoverContacts,
            {
                itemSource: this.targetingSearchService.all$,
                columns: [
                    this.itemNameColumn,
                    this.typeCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.searchTypeFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getSearchTypeFilterCounts(this.allListItems as TargetingSearchList[], this.searchTypeFilter);
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applySearchTypeFitler(filteredItems as TargetingSearchList[], this.searchTypeFilter);
                },
                resetCustomFilters: () => {
                    this.searchTypeFilter.setValue([]);
                }
            }
        ],
        [
            Mode.targetingLists,
            {
                itemSource: this.targetingListService.all$,
                columns: [
                    this.itemNameColumn,
                    this.countCustomColumnModel,
                    this.typeCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.listTypeFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getListTypeFilterCounts(this.allListItems as TargetingListList[], this.listTypeFilter);
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applyListTypeFilters(filteredItems as TargetingListList[], this.listTypeFilter);
                },
                resetCustomFilters: () => {
                    this.listTypeFilter.setValue([]);
                }
            }
        ],
        [
            Mode.distributions,
            {
                itemSource: this.distributionsService.getDistributionSummaries(),
                columns: [
                    this.itemNameColumn,
                    this.statusCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.deliveryStatusFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getDeliveryStatusTypeFilterCounts(this.allListItems as DistributionSummaryModel[], this.deliveryStatusFilter);
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applyStatusFilters(filteredItems as TargetingListList[], this.deliveryStatusFilter);
                },
                resetCustomFilters: () => {
                    this.deliveryStatusFilter.setValue([]);
                }
            }
        ],
    ]);

    constructor(
        usersService: UsersService,
        public sidebarService: DiscoverLeftSidebarService,
        private targetingSearchService: TargetingSearchesService,
        private targetingListService: TargetingListService,
        private distributionsService: DistributionService,
        protected localizationService: LocalizationService,
        public displayGroupsService: DisplayGroupsService
    ) {
        super(usersService, sidebarService, displayGroupsService, localizationService);

        this.subscriptions.push(
            this.sidebarService.getMode().pipe(
                mergeMap((mode) => {
                    this.currentMode = mode;
                    this.currentConfig = this.modeConfigMap.get(this.currentMode);
                    this.setCustomColumnHeadings();
                    this.columnsProvider$.next(this.currentConfig.columns);
                    return this.currentConfig.itemSource;
                }),
                map((items) => {
                    this.allListItems = items;
                })
            ).subscribe(
                () => {
                    this.setUpFilters();
                }
            )
        );
    }

    public setCustomData(rowData: ItemRow[]): ItemRow[] {
        switch (this.currentMode) {
            case Mode.searches:
            case Mode.discoverContacts:
                this.setSavedSearchTypeData(rowData);
                break;
            case Mode.targetingLists:
            case Mode.privateContacts:
                this.setListTypeAndCountData(rowData);
                break;
            case Mode.distributions:
                this.setStatusData(rowData);
                break;
        }
        return rowData;
    }

    private setSavedSearchTypeData(rowData: ItemRow[]) {
        rowData.forEach(row => {
            const item = this.allListItems.find(x => x.id === row.original.id) as TypeLhpItem;
            row.type = this.titleCasePipe.transform(item.type);
        });
    }

    private setListTypeAndCountData(rowData: ItemRow[]) {
        rowData.forEach(row => {
            const item = this.allListItems.find(x => x.id === row.original.id) as TargetingListLhpItem;
            row.count = item.count;
            let type: TargetedListTypesEnum;
            if (item.hasContacts && item.hasOutlets) {
                type = TargetedListTypesEnum.both;
            } else if (item.hasContacts) {
                type = TargetedListTypesEnum.contact;
            } else if (item.hasOutlets) {
                type = TargetedListTypesEnum.outlet;
            } else {
                type = TargetedListTypesEnum.empty;
            }
            row.type = this.localizationService.transform(`Discover.leftSidebar.filter.listType.option.${type}`);
        });
    }

    public handleBulkItemAction(items: AuditableModel[], groups: DisplayGroupModel[], action: ActionsEnum) {
        this.loading$.next(true);
        let service: TargetingSearchesService | TargetingListService;

        switch(this.currentMode) {
            case Mode.targetingLists:
            case Mode.privateContacts:
                service = this.targetingListService;
                break;
            case Mode.searches:
            case Mode.discoverContacts:
                service = this.targetingSearchService;
                break;
        }

        const ids = items.map(x => x.id);

        switch(action) {
            case ActionsEnum.delete: {
                if (this.currentMode === Mode.distributions) {
                    this.distributionsService.confirmBulkDelete(items, groups).subscribe((result) => {
                        this.loading$.next(false);
                        this.resetSelectionProvider$.next(result);
                    });
                } else {
                    service.confirmBulkDelete(items, groups).subscribe((result) => {
                        this.loading$.next(false);
                        this.resetSelectionProvider$.next(result);
                    });
                }

                break;
            }
            case ActionsEnum.hide:
            case ActionsEnum.show: {
                this.resetSelectionProvider$.next(true);

                if (this.currentMode === Mode.distributions) {
                    this.distributionsService.bulkUpdateVisibility(ids, action);
                } else {
                    service.bulkUpdateVisibility(ids, action).subscribe();
                }

                break;
            }
            case ActionsEnum.move: {
                this.displayGroupsService.openGroupSelectionDialog(null, true).subscribe({
                    next: (group: DisplayGroupModel) => {
                        this.resetSelectionProvider$.next(true);

                        if (this.currentMode === Mode.distributions) {
                            this.distributionsService.updateGroup(ids, group);
                        } else if (this.currentMode === Mode.targetingLists || this.currentMode === Mode.privateContacts) {
                            // eslint-disable-next-line rxjs/no-nested-subscribe
                            this.targetingListService.updateGroup(ids, group, true).subscribe();
                        } else if (this.currentMode === Mode.searches || this.currentMode === Mode.discoverContacts) {
                            // eslint-disable-next-line rxjs/no-nested-subscribe
                            this.targetingSearchService.updateGroup(ids, group, true).subscribe();
                        }
                    },
                    complete: () => {
                        this.loading$.next(false);
                    }
                });


                break;
            }
            case ActionsEnum.favorite: {
                this.resetSelectionProvider$.next(true);

                if (this.currentMode === Mode.distributions) {
                    this.distributionsService.updateFavorites(ids, []);
                } else {
                    service.updateFavorites(ids, []).subscribe();
                }

                break;
            }
            case ActionsEnum.unfavorite: {
                this.resetSelectionProvider$.next(true);

                if (this.currentMode === Mode.distributions) {
                    this.distributionsService.updateFavorites([], ids);
                } else {
                    service.updateFavorites([], ids).subscribe();
                }

                break;
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

}
